"use client"

import { fill } from "../../../lib/fill"
import { useAutoleadsLocale, useDateLocale } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import { useMutation } from "@tanstack/react-query"
import {
  Bug,
  CircleCheck,
  CircleHelp,
  Cookie,
  FileText,
  Globe,
  Lock,
  type LucideIcon,
  MailWarning,
  MonitorX,
  Network,
  Server,
  Shield,
  ShieldAlert,
  UserX,
} from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"

import type {
  LeadSecurityAudit,
  SecurityFindingSeverity,
  SecurityReport,
  SecurityServer,
  SecurityThemeReport,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

export const SECURITY_T = {
  securityTitle: { FR: "Audit de sécurité", EN: "Security audit" },
  securityFindingCount: {
    FR: "{n} {n|point|points}",
    EN: "{n} {n|finding|findings}",
  },
  securityScore: { FR: "{score}/100", EN: "{score}/100" },
  securityDisclaimer: {
    FR: "Relevé passif du {date} sur {domain} : DNS, certificat et en-têtes publics uniquement. Pas un test d'intrusion.",
    EN: "Passive check of {domain} on {date}: public DNS, certificate and headers only. Not a penetration test.",
  },
  securityFix: { FR: "Correctif :", EN: "Fix:" },
  securitySectionFindings: { FR: "À corriger", EN: "To fix" },
  securityPositives: { FR: "Déjà en place", EN: "Already in place" },
  securityPositivesHint: {
    FR: "Ce que ce domaine fait déjà bien : à ne jamais vendre contre.",
    EN: "What this domain already does well: never sell against it.",
  },
  securityUnknowns: { FR: "Non vérifié :", EN: "Not verified:" },
  securityCleanThemes: { FR: "Rien à signaler :", EN: "Nothing to report:" },
  securityNoFinding: {
    FR: "Aucun point à corriger sur ce domaine.",
    EN: "Nothing to fix on this domain.",
  },
  securitySeverityHigh: { FR: "Urgent", EN: "Urgent" },
  securitySeverityMedium: { FR: "À surveiller", EN: "Worth watching" },
  securitySeverityLow: { FR: "Mineur", EN: "Minor" },
  securityWebUnavailable: {
    FR: "Le site n'a pas répondu : seuls le domaine et la messagerie ont été vérifiés.",
    EN: "The site did not answer: only the domain and email setup were checked.",
  },
  securityUnavailable: {
    FR: "Audit impossible : le domaine n'a rien répondu.",
    EN: "Audit not possible: the domain did not answer.",
  },
  securityThemeEmailSpoofing: {
    FR: "Usurpation des e-mails",
    EN: "Email spoofing",
  },
  securityThemeEncryption: { FR: "Chiffrement", EN: "Encryption" },
  securityThemeOutdatedStack: {
    FR: "Logiciels vulnérables",
    EN: "Vulnerable software",
  },
  securityThemeWebHardening: {
    FR: "Protections du site",
    EN: "Site hardening",
  },
  securityThemeDomain: { FR: "Nom de domaine", EN: "Domain name" },
  securityThemePrivacy: { FR: "Données personnelles", EN: "Personal data" },
  securityThemeAttackSurface: {
    FR: "Surface d'attaque",
    EN: "Attack surface",
  },
  securityThemeImpersonation: {
    FR: "Usurpation de marque",
    EN: "Brand impersonation",
  },
  securityThemeHosting: { FR: "Hébergement", EN: "Hosting" },
  securityThemeReputation: { FR: "Réputation", EN: "Reputation" },
  securityThemeExposure: { FR: "Services exposés", EN: "Exposed services" },
  securityPdf: { FR: "PDF", EN: "PDF" },
  securityServers: {
    FR: "Serveurs de l'entreprise vérifiés",
    EN: "Business servers checked",
  },
  securityServersHint: {
    FR: "Ports ouverts relevés par Censys, sans scan de notre part. En rouge : accès à distance, base de données ou FTP.",
    EN: "Open ports recorded by Censys, with no scan on our side. In red: remote access, database or FTP.",
  },
  securityPdfFailed: {
    FR: "Le PDF n'a pas pu être généré.",
    EN: "The PDF could not be generated.",
  },
  securityFilterAny: { FR: "Toutes", EN: "Any" },
  securityFilterExposed: { FR: "Exposés (D à F)", EN: "Exposed (D to F)" },
  securityFilterSpoofable: {
    FR: "E-mails usurpables",
    EN: "Spoofable email",
  },
  securityFilterSolid: { FR: "Solides (A, B)", EN: "Solid (A, B)" },
  securityBenchmark: {
    FR: "Classé {rank} sur {total} « {keyword} » audités (1 = le mieux protégé).",
    EN: 'Ranked {rank} of {total} audited "{keyword}" businesses (1 = best protected).',
  },
  securityChanges: {
    FR: "Depuis le relevé du {date} ({score}/100) : {fixed} corrigé(s), {still} toujours présent(s), {new} nouveau(x).",
    EN: "Since the check on {date} ({score}/100): {fixed} fixed, {still} still open, {new} new.",
  },
  securityMeasures: { FR: "Plan d'action", EN: "Action plan" },
  securityMeasuresHint: {
    FR: "Les correctifs à faire en premier, du plus urgent au plus simple.",
    EN: "The fixes to make first, most urgent first.",
  },
  securityInsurer: {
    FR: "Ce qu'un assureur cyber relèverait",
    EN: "What a cyber insurer would flag",
  },
  securityInsurerHint: {
    FR: "Les points qu'un scan avant devis d'assurance cyber fait corriger avant de couvrir.",
    EN: "What a cyber insurer's pre-quote scan asks to fix before covering.",
  },
} as const

type SecurityKey = keyof typeof SECURITY_T
type SecurityTr = (key: SecurityKey) => string

const THEMES: Record<string, { labelKey: SecurityKey; icon: LucideIcon }> = {
  email_spoofing: { labelKey: "securityThemeEmailSpoofing", icon: MailWarning },
  encryption: { labelKey: "securityThemeEncryption", icon: Lock },
  outdated_stack: { labelKey: "securityThemeOutdatedStack", icon: Bug },
  web_hardening: { labelKey: "securityThemeWebHardening", icon: Shield },
  domain: { labelKey: "securityThemeDomain", icon: Globe },
  privacy: { labelKey: "securityThemePrivacy", icon: Cookie },
  attack_surface: { labelKey: "securityThemeAttackSurface", icon: Network },
  impersonation: { labelKey: "securityThemeImpersonation", icon: UserX },
  hosting: { labelKey: "securityThemeHosting", icon: Server },
  reputation: { labelKey: "securityThemeReputation", icon: ShieldAlert },
  exposure: { labelKey: "securityThemeExposure", icon: MonitorX },
}

// Same palette as the SEO audit's severity steps: red urgent, amber worth
// watching, muted for the minor hygiene misses.
const SEVERITY_STYLE: Record<
  SecurityFindingSeverity,
  { labelKey: SecurityKey; dot: string; chip: string }
> = {
  high: {
    labelKey: "securitySeverityHigh",
    dot: "bg-destructive",
    chip: "bg-destructive/10 text-destructive",
  },
  medium: {
    labelKey: "securitySeverityMedium",
    dot: "bg-warning",
    chip: "bg-warning/15 text-warning-text",
  },
  low: {
    labelKey: "securitySeverityLow",
    dot: "bg-muted-foreground",
    chip: "bg-muted text-foreground",
  },
}

const SEVERITY_ORDER: SecurityFindingSeverity[] = ["high", "medium", "low"]

// Green for a well-kept domain, red for an easy target, neutral in between: it
// describes the lead's situation, not our sales interest.
function gradeTileClass(grade: string): string {
  if (grade === "A" || grade === "B")
    return "border-success/20 bg-success/10 text-success"
  if (grade === "E" || grade === "F")
    return "border-destructive/20 bg-destructive/10 text-destructive"
  return "border-border bg-muted text-foreground"
}

function themeLabel(key: string, tr: SecurityTr): string {
  const theme = THEMES[key]
  return theme ? tr(theme.labelKey) : key
}

// The report in the app's language, the other one when it is all there is.
function securityReportFor(
  audit: LeadSecurityAudit,
  locale: string,
): SecurityReport | null {
  const reports = audit.report
  if (!reports) return null
  return (locale === "EN" ? reports.en : reports.fr) ?? reports.fr ?? null
}

export function LeadSecurityTitle() {
  return <>{useTr(SECURITY_T)("securityTitle")}</>
}

// The accordion row's meta: the grade and how many findings it carries.
export function LeadSecurityMeta({ audit }: { audit: LeadSecurityAudit }) {
  const tr = useTr(SECURITY_T)
  const pluralLocale = useDateLocale()
  const report = securityReportFor(audit, useAutoleadsLocale())
  if (!report) return null
  return (
    <>
      {report.grade}
      {", "}
      {fill(tr("securityFindingCount"), { n: report.count }, pluralLocale)}
    </>
  )
}

// Master-detail like the SEO audit: the themes with something to fix in a rail
// on the left (a chip row on a phone), the selected theme's findings beside,
// and what the domain already does well in a column of its own on the right.
export function LeadSecurityReport({ audit }: { audit: LeadSecurityAudit }) {
  const tr = useTr(SECURITY_T)
  const locale = useAutoleadsLocale()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const report = securityReportFor(audit, locale)
  if (audit.unavailable || !report) {
    return <p className="text-muted-foreground">{tr("securityUnavailable")}</p>
  }
  const problems = report.themes
    .filter((theme) => theme.items.length > 0)
    .sort(
      (a, b) =>
        SEVERITY_ORDER.indexOf(a.severity || "low") -
        SEVERITY_ORDER.indexOf(b.severity || "low"),
    )
  const clean = report.themes.filter((theme) => theme.items.length === 0)
  const selected =
    problems.find((theme) => theme.key === selectedKey) ?? problems[0]
  const checkedAt = formatDate(audit.checked_at, locale)
  const hasSide = report.positives.length > 0 || clean.length > 0
  const railEntry = (theme: SecurityThemeReport, chip: boolean) => (
    <ThemeRailRow
      key={theme.key}
      theme={theme}
      active={theme.key === selected?.key}
      onSelect={() => setSelectedKey(theme.key)}
      tr={tr}
      chip={chip}
    />
  )
  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-md border text-base font-semibold",
            gradeTileClass(report.grade),
          )}
        >
          {report.grade}
        </span>
        <div className="min-w-0">
          {report.score !== null && (
            <p className="text-foreground text-2sm font-medium tabular-nums">
              {tr("securityScore").replace("{score}", String(report.score))}
            </p>
          )}
          <p className="text-muted-foreground leading-snug">
            {tr("securityDisclaimer")
              .replace("{domain}", audit.domain ?? "")
              .replace("{date}", checkedAt)}
          </p>
          {audit.benchmark && (
            <p className="text-foreground leading-snug">
              {tr("securityBenchmark")
                .replace("{rank}", String(audit.benchmark.rank))
                .replace("{total}", String(audit.benchmark.total))
                .replace("{keyword}", audit.benchmark.keyword)}
            </p>
          )}
          {report.changes && (
            <p className="text-foreground leading-snug">
              {tr("securityChanges")
                .replace("{date}", formatDate(report.changes.since, locale))
                .replace(
                  "{score}",
                  String(report.changes.previous_score ?? "?"),
                )
                .replace("{fixed}", String(report.changes.fixed.length))
                .replace("{still}", String(report.changes.still.length))
                .replace("{new}", String(report.changes.new.length))}
            </p>
          )}
        </div>
      </div>
      {audit.coverage?.web === "unavailable" && (
        <p className="text-muted-foreground">{tr("securityWebUnavailable")}</p>
      )}
      <div
        className={cn(
          "flex flex-col gap-4",
          hasSide && "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,16rem)]",
        )}
      >
        {problems.length > 0 && selected ? (
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,13rem)_1fr]">
            <ul className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:hidden">
              {problems.map((theme) => railEntry(theme, true))}
            </ul>
            <div className="hidden flex-col sm:flex sm:border-r sm:pr-3">
              <p className="text-muted-foreground mb-1 px-1.5">
                {tr("securitySectionFindings")}
              </p>
              <ul className="flex flex-col gap-0.5">
                {problems.map((theme) => railEntry(theme, false))}
              </ul>
            </div>
            <div className="sm:-ml-1.5 sm:h-80 sm:overflow-x-hidden sm:overflow-y-auto sm:pr-1.5 sm:pl-1.5">
              <ThemeDetail theme={selected} tr={tr} />
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">{tr("securityNoFinding")}</p>
        )}
        {hasSide && (
          <div
            className={cn(
              problems.length > 0 &&
                "border-border/70 border-t pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-4",
            )}
          >
            <PositivesColumn
              positives={report.positives}
              clean={clean}
              tr={tr}
            />
          </div>
        )}
      </div>
      {((report.measures?.length ?? 0) > 0 ||
        (report.insurer_flags?.length ?? 0) > 0) && (
        <div className="border-border/70 grid gap-4 border-t pt-3 sm:grid-cols-2">
          {report.measures && report.measures.length > 0 && (
            <ActionPlan measures={report.measures} tr={tr} />
          )}
          {report.insurer_flags && report.insurer_flags.length > 0 && (
            <InsurerFlags flags={report.insurer_flags} tr={tr} />
          )}
        </div>
      )}
      {audit.servers && audit.servers.length > 0 && (
        <ServersChecked servers={audit.servers} tr={tr} />
      )}
      {report.unknowns.length > 0 && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-1 border-t pt-1.5">
          <CircleHelp className="size-3 shrink-0" aria-hidden />
          <span>{tr("securityUnknowns")}</span>
          {report.unknowns.map((unknown) => (
            <span
              key={unknown}
              className="border-border/60 rounded-sm border px-1 py-0.5"
            >
              {unknown}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function ThemeRailRow({
  theme,
  active,
  onSelect,
  tr,
  chip,
}: {
  theme: SecurityThemeReport
  active: boolean
  onSelect: () => void
  tr: SecurityTr
  chip: boolean
}) {
  return (
    <li className={cn(chip && "shrink-0")}>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active}
        className={cn(
          "focus-visible:ring-ring flex items-center text-left text-xs transition-colors outline-none focus-visible:ring-2",
          chip
            ? "gap-1.5 rounded-full border px-2.5 py-1 whitespace-nowrap"
            : "w-full gap-2 rounded-md border border-transparent px-1.5 py-1",
          chip &&
            (active
              ? "border-foreground/20 bg-accent text-foreground font-medium"
              : "border-border text-muted-foreground hover:bg-accent/50"),
          !chip &&
            (active
              ? "border-border/70 bg-background font-medium shadow-xs"
              : "hover:bg-accent/50"),
        )}
      >
        <span
          aria-hidden
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            SEVERITY_STYLE[theme.severity || "low"].dot,
          )}
        />
        <span className={cn(!chip && "min-w-0 flex-1 truncate")}>
          {themeLabel(theme.key, tr)}
        </span>
        <span className="text-muted-foreground shrink-0 tabular-nums">
          {theme.items.length}
        </span>
      </button>
    </li>
  )
}

// The pane: the theme header (icon tile and pill tinted by its worst finding,
// the name, its grade and count), then the findings as flat rows split by
// hairlines, each with its fix under it.
function ThemeDetail({
  theme,
  tr,
}: {
  theme: SecurityThemeReport
  tr: SecurityTr
}) {
  const pluralLocale = useDateLocale()
  const style = SEVERITY_STYLE[theme.severity || "low"]
  const Icon = THEMES[theme.key]?.icon ?? Shield
  return (
    <div className="flex flex-col gap-2">
      <div className="border-border/70 flex items-start gap-2 border-b pb-3">
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-md",
            style.chip,
          )}
        >
          <Icon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-2sm truncate font-medium">
            {themeLabel(theme.key, tr)}
          </p>
          <p className="text-muted-foreground truncate">
            {theme.grade}
            {" · "}
            {fill(
              tr("securityFindingCount"),
              { n: theme.items.length },
              pluralLocale,
            )}
          </p>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 font-medium",
            style.chip,
          )}
        >
          {tr(style.labelKey)}
        </span>
      </div>
      <ul className="divide-border/70 flex flex-col divide-y">
        {theme.items.map((item, index) => (
          // A mail check repeats once per audited domain, so the code alone
          // is not unique.
          <li
            key={`${item.code}-${index}`}
            className="py-2 first:pt-0 last:pb-0"
          >
            <p className="text-foreground flex items-center gap-1.5 font-medium">
              <span
                aria-hidden
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  SEVERITY_STYLE[item.severity].dot,
                )}
              />
              {item.title}
            </p>
            <p className="text-muted-foreground leading-snug">{item.detail}</p>
            <p className="text-muted-foreground mt-0.5 leading-snug">
              <span className="text-foreground">{tr("securityFix")}</span>{" "}
              {item.fix}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

// What already works, beside the findings: a rep must see it so the pitch never
// argues against a protection the prospect already has.
function PositivesColumn({
  positives,
  clean,
  tr,
}: {
  positives: string[]
  clean: SecurityThemeReport[]
  tr: SecurityTr
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-foreground text-2sm font-medium">
          {tr("securityPositives")}
        </p>
        <p className="text-muted-foreground leading-snug">
          {tr("securityPositivesHint")}
        </p>
      </div>
      {positives.length > 0 && (
        <ul className="flex flex-col gap-1">
          {positives.map((positive) => (
            <li key={positive} className="text-foreground flex gap-1.5">
              <CircleCheck
                className="text-success mt-px size-3.5 shrink-0"
                aria-hidden
              />
              <span className="min-w-0 flex-1">{positive}</span>
            </li>
          ))}
        </ul>
      )}
      {clean.length > 0 && (
        <p className="text-muted-foreground leading-snug">
          {tr("securityCleanThemes")}{" "}
          {clean.map((theme) => themeLabel(theme.key, tr)).join(", ")}
        </p>
      )}
    </div>
  )
}

function formatDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale === "EN" ? "en-US" : "fr-FR")
}

// The fixes to make first, numbered: the "6 measures" format French SMEs know
// from the ANSSI's MonAideCyber.
function ActionPlan({
  measures,
  tr,
}: {
  measures: NonNullable<SecurityReport["measures"]>
  tr: SecurityTr
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-foreground text-2sm font-medium">
          {tr("securityMeasures")}
        </p>
        <p className="text-muted-foreground leading-snug">
          {tr("securityMeasuresHint")}
        </p>
      </div>
      <ol className="flex flex-col gap-1.5">
        {measures.map((measure, index) => (
          <li key={measure.code} className="flex gap-2">
            <span className="text-muted-foreground w-3 shrink-0 text-right tabular-nums">
              {index + 1}
            </span>
            <span className="min-w-0 flex-1">
              <span className="text-foreground">{measure.fix}</span>
              <span className="text-muted-foreground block leading-snug">
                {measure.title}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

// The findings an insurer's pre-quote scan would make the prospect fix: the
// hook for a broker, and a reason to act that is not ours.
function InsurerFlags({ flags, tr }: { flags: string[]; tr: SecurityTr }) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-foreground text-2sm font-medium">
          {tr("securityInsurer")}
        </p>
        <p className="text-muted-foreground leading-snug">
          {tr("securityInsurerHint")}
        </p>
      </div>
      <ul className="flex flex-col gap-1">
        {flags.map((flag) => (
          <li key={flag} className="text-foreground flex gap-1.5">
            <span
              aria-hidden
              className="bg-destructive mt-1.5 size-1.5 shrink-0 rounded-full"
            />
            <span className="min-w-0 flex-1">{flag}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export type SecurityFilter = "any" | "exposed" | "spoofable" | "solid"

const EXPOSED_GRADES = new Set(["D", "E", "F"])
const SOLID_GRADES = new Set(["A", "B"])
// No SPF, no DMARC, or a DMARC that does not block: anyone can write as them.
const SPOOFABLE_CODES = new Set([
  "spf_missing",
  "spf_permissive",
  "dmarc_missing",
  "dmarc_not_enforced",
])

// The leads list's security filter. A lead the audit never reached matches
// only "any": an unknown is never shown as exposed, nor as solid.
export function matchesSecurityFilter(
  audit: LeadSecurityAudit | null,
  filter: SecurityFilter,
): boolean {
  if (filter === "any") return true
  const report = audit?.report?.fr ?? audit?.report?.en
  if (!audit || audit.unavailable || !report) return false
  if (filter === "exposed") return EXPOSED_GRADES.has(report.grade)
  if (filter === "solid") return SOLID_GRADES.has(report.grade)
  return report.themes.some((theme) =>
    theme.items.some((item) => SPOOFABLE_CODES.has(item.code)),
  )
}

// The branded PDF to send the prospect. The tab opens on the click itself (a
// tab opened after the request returns would be blocked as a popup), then
// lands on the file once it is rendered.
export function SecurityPdfButton({ leadId }: { leadId: number }) {
  const tr = useTr(SECURITY_T)
  const pdf = useMutation({
    mutationFn: () =>
      apiClient<{ url: string }>({
        url: `${apiUrl("api/al/security_report/lead_pdf/")}?lead_id=${leadId}`,
        method: "GET",
      }),
  })
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      disabled={pdf.isPending}
      onClick={() => {
        const tab = window.open("", "_blank")
        pdf.mutate(undefined, {
          onSuccess: ({ url }) => {
            if (tab) tab.location.href = url
            else window.open(url, "_blank", "noopener")
          },
          onError: () => {
            tab?.close()
            toast.error(tr("securityPdfFailed"))
          },
        })
      }}
    >
      <FileText />
      {tr("securityPdf")}
    </Button>
  )
}

// The business's own servers and every port Censys found open on them: the
// risky ones in red, the rest plain, so the rep sees exactly what was read.
function ServersChecked({
  servers,
  tr,
}: {
  servers: SecurityServer[]
  tr: SecurityTr
}) {
  return (
    <div className="border-border/70 flex flex-col gap-2 border-t pt-3">
      <div>
        <p className="text-foreground text-2sm font-medium">
          {tr("securityServers")}
        </p>
        <p className="text-muted-foreground leading-snug">
          {tr("securityServersHint")}
        </p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {servers.map((server) => (
          <li
            key={server.ip}
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
          >
            <span className="text-foreground font-medium">{server.host}</span>
            <span className="text-muted-foreground tabular-nums">
              {server.ip}
            </span>
            {server.services.map((service) => (
              <span
                key={`${service.port}-${service.protocol}`}
                className={cn(
                  "rounded-sm border px-1 py-0.5 tabular-nums",
                  service.risky
                    ? "border-destructive/30 bg-destructive/10 text-destructive"
                    : "border-border/60 text-muted-foreground",
                )}
              >
                {service.port}
                {service.protocol && service.protocol !== "UNKNOWN"
                  ? ` ${service.protocol}`
                  : ""}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}
